
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { WorkoutCard } from "./components/WorkoutCard";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguagePath } from "@/utils/language";

export const StudentWorkoutsList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useAuth();
  const { t } = useTranslations("workouts");
  const { getPath } = useLanguagePath();

  // Buscar o perfil do aluno para obter o ID
  const {
    data: studentProfile,
    isLoading: isLoadingProfile
  } = useQuery({
    queryKey: ["student-profile-for-workouts", user?.id, user?.email],
    queryFn: async () => {
      if (!user?.id && !user?.email) return null;
      console.log("Buscando perfil do aluno para treinos:", user.email || user.id);

      // REVERTIDO: Primeiro tentar por email, depois por auth_id
      let data = null;
      let error = null;

      // Primeiro tentamos buscar pelo email (mais confiável para estudantes manuais)
      if (user.email) {
        const { data: emailResult, error: emailError } = await supabase
          .from("students")
          .select("*")
          .eq("email", user.email)
          .eq("status", "active")
          .maybeSingle();
        
        if (emailResult) {
          data = emailResult;
        }
      }

      // Se não encontrou pelo email, tenta pelo auth_id (estudantes registados)
      if (!data && user.id) {
        const { data: authIdResult, error: authIdError } = await supabase
          .from("students")
          .select("*")
          .eq("auth_id", user.id)
          .eq("status", "active")
          .maybeSingle();
        
        if (authIdResult) {
          data = authIdResult;
        } else {
          error = authIdError;
        }
      }

      if (error) {
        console.error("Erro ao buscar aluno:", error);
      }
      if (data) {
        console.log("Aluno encontrado:", data.id);
        return data;
      }
      
      console.log("Aluno não encontrado para treinos");
      return null;
    },
    enabled: !!(user?.id || user?.email),
    staleTime: 60000 // 1 minuto
  });

  // Buscar os treinos do aluno
  const {
    data: workouts,
    isLoading: isLoadingWorkouts
  } = useQuery({
    queryKey: ["student-workouts", studentProfile?.id],
    queryFn: async () => {
      if (!studentProfile?.id) return [];
      console.log("Buscando treinos para o aluno:", studentProfile.id);
      
      // First get custom exercises to mark them
      const { data: customExercises, error: customError } = await supabase
        .from("custom_exercises")
        .select("name")
        .eq("trainer_id", studentProfile.trainer_id);
        
      if (customError) {
        console.error("Erro ao buscar exercícios personalizados:", customError);
      }
      
      const customExerciseNames = customExercises?.map(ce => ce.name.toLowerCase()) || [];

      const { data, error } = await supabase
        .from("workouts")
        .select(`
          *,
          exercises(
            *
          )
        `)
        .eq("target_student_id", studentProfile.id)
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar treinos:", error);
        return [];
      }

      // Mark custom exercises
      const workoutsWithCustomFlag = data.map(workout => {
        if (workout.exercises) {
          workout.exercises = workout.exercises.map(exercise => ({
            ...exercise,
            isCustom: customExerciseNames.includes(exercise.name?.toLowerCase() || '')
          }));
        }
        return workout;
      });
      
      console.log("Treinos encontrados:", workoutsWithCustomFlag);
      return workoutsWithCustomFlag || [];
    },
    enabled: !!studentProfile?.id,
    staleTime: 60000 // 1 minuto
  });
  
  const isLoading = isLoadingProfile || isLoadingWorkouts;

  // Filtrar os treinos com base no termo de pesquisa
  const filteredWorkouts = workouts?.filter(workout => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return workout.title?.toLowerCase().includes(term) || workout.description?.toLowerCase().includes(term) || workout.type?.toLowerCase().includes(term);
  });
  
  return <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100/50 dark:border-gray-700/50">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight gradient-text">{t("title")}</h2>
          <p className="text-muted-foreground dark:text-gray-400">{t("subtitle")}</p>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/student-dashboard")} className="text-flame hover:text-flame/80">Dashboard</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium dark:text-white">{t("title")}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      <div className="relative flex-1 w-full sm:max-w-xs">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder={t("actions.search")} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8 bg-white/90 border border-slate-100 rounded-xl shadow-sm focus:ring-1 focus:ring-flame/20 dark:bg-gray-800/80 dark:border-gray-700 dark:text-white dark:placeholder:text-gray-500" />
      </div>

      {isLoading ? <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground dark:text-gray-400" />
        </div> : !filteredWorkouts?.length ? <div className="bg-white/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/50 overflow-hidden hover:shadow-lg transition-all dark:bg-gray-800/90 dark:border-gray-700/50">
          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader>
              <CardTitle className="dark:text-white">{t("title")}</CardTitle>
              <CardDescription className="dark:text-gray-300">
                Você ainda não tem nenhum treino associado.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground dark:text-gray-400">
                Contacte seu personal trainer para adicionar um plano de treino personalizado.
              </p>
            </CardContent>
          </Card>
        </div> : <div className="flex flex-col gap-4">
          {filteredWorkouts.map(workout => <WorkoutCard key={workout.id} workout={workout} />)}
        </div>}
    </div>;
};
